import removeAccents from 'remove-accents';
import breedIndexRules, { BreedIndexRule, BreedIndexRuleConfig } from 'config/breedIndexRules';
import { BullListItem, Segment } from 'types';

type IndexWithValue = { name: string; value: string };

const normalize = (text: string) => removeAccents(`${text ?? ''}`.trim().toLowerCase());
const normalizeType = (text: string) => normalize(text).replace(/[^a-z]/g, '');
const getSummaryIndices = (bull: BullListItem): IndexWithValue[] =>
  'indices' in bull && Array.isArray(bull.indices) ? bull.indices : [];

const getSegmentFromBull = (bull: BullListItem): Segment => {
  const { type, segment } = bull || {};
  if (type === '1' && segment === '1') return 'ec';
  if (type === '1' && segment === '2') return 'el';
  if (type === '2' && segment === '1') return 'zc';
  if (type === '2' && segment === '2') return 'zl';
  return '';
};

const findIndexValue = (
  bull: BullListItem,
  targetIndex: BreedIndexRule
): string | null => {
  const targetName = normalize(targetIndex.name);
  const targetType = normalizeType(targetIndex.valueType);

  for (const proofEntry of 'proof' in bull ? bull.proof ?? [] : []) {
    for (const proofObj of Object.values(proofEntry ?? {})) {
      const table = (proofObj as any)?.table;
      if (!Array.isArray(table)) continue;
      for (const section of table) {
        if (
          !section ||
          !Array.isArray(section.headers) ||
          !Array.isArray(section.type) ||
          !Array.isArray(section.values)
        ) continue;

        const headerIndex = section.headers.findIndex(
          (h: string) => normalize(h) === targetName
        );
        if (headerIndex === -1) continue;

        const typeIndex = section.type.findIndex(
          (t: string) => normalizeType(t) === targetType
        );
        if (typeIndex === -1) continue;

        const row = section.values?.[headerIndex];
        if (row == null) continue;

        const value = Array.isArray(row) ? row[typeIndex] : row;
        if (value === undefined || value === null || `${value}` === '') continue;

        return `${value}`;
      }
    }
  }
  return null;
};

const getConfigForBull = (bull: BullListItem): BreedIndexRuleConfig | null => {
  const breedKey = normalize(bull?.breed ?? '');
  const config = breedIndexRules[breedKey];
  if (!config) return null;
  const segment = getSegmentFromBull(bull);
  if (segment !== config.segment) return null;
  return config;
};

export const getBullIndicesWithValues = (bull: BullListItem): IndexWithValue[] => {
  const config = getConfigForBull(bull);
  if (!config) return [];

  const summaryIndices = getSummaryIndices(bull);
  let r = summaryIndices.length > 0
    ? config.indices
      .map((idx) => {
        const summaryIndex = summaryIndices.find((entry) =>
          normalize(entry.name) === normalize(idx.name)
        );
        if (!summaryIndex) return null;
        return { name: idx.name, value: summaryIndex.value };
      })
      .filter(Boolean) as IndexWithValue[]
    : config.indices
      .map((idx) => {
        const value = findIndexValue(bull, idx);
        if (value === null) return null;
        return { name: idx.name, value };
      })
      .filter(Boolean) as IndexWithValue[];

  const proof = 'proof' in bull ? bull.proof : undefined;
  const hasDeltaG = proof?.[0] &&
  Object.keys(proof[0]).some(key =>
    key.includes('CONEXÃO DELTA G')
  );

  const hasNatura = proof?.[0] &&
  Object.keys(proof[0]).some(key =>
    key.includes('NATURA')
  );

  if (proof) {
    if (hasDeltaG) {
      r = r.filter(item => item.name !== 'Ind.DESM');
      r = r.filter(item => item.name !== 'Ind.FINAL');
    } else if (hasNatura) {
      r = r.filter(item => item.name !== 'GND');
    } else {
      r = r.filter(item => item.name !== 'GND');
      r = r.filter(item => item.name !== 'Ind.DESM');
      r = r.filter(item => item.name !== 'Ind.FINAL');
    }
  }

  return r;

};

export const getBullIndices = (bull: BullListItem): string[] =>
  getBullIndicesWithValues(bull).map((idx) => idx.name);

export const getConfiguredBullIndices = (bull: BullListItem): string[] => {
  const config = getConfigForBull(bull);
  if (!config) return [];

  const summaryIndices = getSummaryIndices(bull);
  if (summaryIndices.length > 0) {
    return config.indices
      .filter((idx) =>
        summaryIndices.some((entry) => normalize(entry.name) === normalize(idx.name))
      )
      .map((idx) => idx.name);
  }

  return config.indices.map((idx) => idx.name);
};

export const bullHasIndex = (bull: BullListItem, target: string | null): boolean => {
  if (!target || target === 'all' || target === '-') return true;
  const targetNormalized = normalize(target);
  return getBullIndices(bull).some((idx) => normalize(idx) === targetNormalized);
};

export default {
  getBullIndices,
  getConfiguredBullIndices,
  getBullIndicesWithValues,
  bullHasIndex
};
